import type { CostRate } from 'api/cost-rates';
import type { PartnerPermission } from 'api/partner/types';
import type {
  FieldAccessMeta,
  FieldAccessMetaInBase3,
  FieldValue,
  FieldValueType,
} from 'api/tasks';
import type { TimeoffResponse } from 'api/timeoff/types';
import type { FormResponse } from 'components/organisms/forms/utils';
import type { ColumnOrder } from 'components/organisms/grid-custom-column';
import { Minutes } from 'components/organisms/user-capacity';
import { TemplateId, TemplateObjectType } from 'data/templates/api';
import type { DocumentTemplate } from 'data/templates/documents';
import type { ProjectTemplateApi } from 'data/templates/projects';

// TODO:: Use MemberType in places instead of UserType. Later extend from this if additional
export enum MemberType {
  Native = 'NATIVE',
  External = 'EXTERNAL',
  Partner = 'PARTNER',
}

export interface Team {
  teamId: number;
  teamName: string;
  teamType: MemberType;
  teamDescription?: string;
  roleId?: number;
  projectId?: number;
  companyList?: AtLeast<Company, 'companyId'>[];
  userList?: User[];
  projectIds?: number[];
  accountLevelTeam?: boolean;
  resolved?: boolean;
}
export interface TeamMeta {
  totalTask: number;
  privateTask: number;
  publicTask: number;
}
export interface TeamWithMeta extends Team {
  meta: TeamMeta;
}
export enum RoleType {
  Vendor = 'VENDOR',
  Customer = 'CUSTOMER',
}
export interface Role {
  roleId: number;
  roleName: string;
  roleDescription?: string;
  usedByPartners?: boolean;
  type: RoleType;
}
export enum LoginPolicyType {
  RocketlaneAuth = 'ROCKETLANE_AUTH',
  MagicLink = 'MAGIC_LINK',
  Idp = 'IDP',
}
export interface BaseAuthenticationType<T> {
  companyId: number;
  authType: T;
  mandatory: boolean;
  enabled: boolean;
  default: boolean;
}
interface IdpAuthenticationType
  extends BaseAuthenticationType<LoginPolicyType.Idp> {
  idpConfigId: number;
}

export type AuthenticationType =
  | IdpAuthenticationType
  | BaseAuthenticationType<LoginPolicyType.MagicLink>
  | BaseAuthenticationType<LoginPolicyType.RocketlaneAuth>;
export enum CompanyType {
  Customer = 'CUSTOMER',
  Partner = 'PARTNER',
  Vendor = 'VENDOR',
}
export interface Company {
  companyName: string;
  companyId: number;
  companyLogoUrl?: string;
  companyLogo?: string;
  companyUrl?: string;
  authentications?: AuthenticationType[];
  capacityMinutes: Minutes;
  companyType: CompanyType;
  accessLevel?: PartnerPermission; // this is only applicable to partner company
  strategic?: boolean;
  canTrackTime?: boolean; // this is only applicable to partner company
}

export enum UserType {
  Native = 'NATIVE',
  External = 'EXTERNAL',
  Partner = 'PARTNER',
}

/**
 * @deprecated
 *
 * Use `ProjectPermissionSetType` and `AccountPermissionSetType` instead
 */
export enum PermissionSetType {
  AccountAdmin = 1,
  ProjectOwner,
  Collaborator,
  CustomerCollaborator,
  CustomerChampion,
  Observer,
  SuperUser,
  AccountVendorCollaborator,
  AccountCustomerCollaborator = 10,
  PartnerAdmin = 11,
  PartnerMember = 12,
  PartnerCollaborator = 13,
  IcertisPmAccountRole = 100,
}

export enum UserStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Invited = 'INVITED',
}

export interface UserGroup {
  userGroupsId: number;
  userGroupName: string;
}

export interface User {
  userTimeOffDetails?: TimeoffResponse[];
  createdAt?: number;
  firstName?: string;
  lastName?: string;
  emailId: string;
  userId: number;
  photoUrl?: string;
  userType: UserType;
  company: Company;
  role?: Role;
  userStatus: UserStatus;
  timeZone?: string;
  fields?: Array<FieldValue<FieldValueType>>;
  defaultCapacity: boolean;
  capacityMinutes: Minutes; // weekly capacity
  projectIds?: Array<number>;
  timeSheetApprover?: number;
  holidayCalendarId?: number;
  costRate?: CostRate;
  reportingTo?: number;
  managerOf?: number[];
  userGroups: UserGroup[];
  permissionSet?: {
    permissionSetId: number;
    permissionSetName: string;
    permissionSetType: number;
  };
  fieldsAccessMeta?: FieldAccessMeta;
  fieldsAccessMetaInBase3?: FieldAccessMetaInBase3;
}

export interface Session {
  sessionPayload: {
    user: User;
    permissionSet: PermissionSet;
  };
}

export interface SignupSession {
  sessionToken: string;
}

/**
 * @deprecated
 * Use PermissionSet from `stores/permission-sets` instead
 */
export interface PermissionSet {
  permissionSetId: number;
  permissionSetName: string;
  permissionSetType: PermissionSetType;
}
export interface ProjectMember extends User {
  projects?: {
    [key: number]: {
      permissionSet: PermissionSet;
      invitationStatus: {
        active?: boolean;
        joinedAt?: number;
      };
    };
  };
}
export interface Squad {
  users: User[];
  teams: Team[];
}

// TODO:: Move this to proper type instead of number or pull it from models/tokens.
export type Timestamp = number;
export type DateType = string; // YYYY-MM-DD

export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

export const DISPLAY_FORMAT = 'DD MMM YY, hh:mm A';
export const DATE_INPUT_DISPLAY_FORMAT = 'DD MMM YYYY';

// https://stackoverflow.com/a/57572747/5277189
type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> &
  U[keyof U];

export type BulkActionPayload<T extends unknown> = AtLeastOne<{
  create: T[];
  update: T[];
  delete: T[];
}>;

export enum OrderType {
  AllTasks = 'all_tasks',
  ProjectList = 'project_list',
  ProjectTaskList = 'project_task_list',
  DrilldownList = 'drilldown_list',
}

export interface Order {
  key: string;
  viewId: number;
  columnOrder: ColumnOrder[];
  orderType: OrderType;
  createdAt?: number;
}

export interface FormResponseDraft {
  response: FormResponse;
  timestamp: number;
  formInstance: number;
}

export interface TemplateDraft {
  content: ProjectTemplateApi | DocumentTemplate;
  timestamp: number;
  templateId: TemplateId;
  templateType: TemplateObjectType;
}
